import config from "config";
import { Platform } from "react-native";
import { defaultFAImage } from "screens/common/constants";
import {
  documentTemplate,
  experienceMapping,
  faImageMapping,
  noticePeriodMapping,
  proficiencyMapping,
  qualificationOrdering
} from "screens/Profile/constants";
import { pushClevertapEvent } from "utils/clevertapEvents";
import getData from "utils/getData";
import VernacManager from "utils/vernacular";

export const validateFileSize = (file, defaultSizeLimit = 5) =>
  file && !(file.size > 1024 * 1024 * defaultSizeLimit);

export const validateNFileSize = (size, defaultSizeLimit = 5) =>
  size && !(size > 1024 * 1024 * defaultSizeLimit);

export const validateFileType = (file, regex) => file && regex.test(file.name);

export const sortByIsOwned = (first, second) => {
  if (first.is_owned === second.is_owned) {
    return 0;
  }
  if (first.is_owned) {
    return -1;
  }
  return 1;
};

export const sortByTemplate = (first = {}, second = {}) => {
  if ("template" in first && "template" in second) {
    return 0;
  }
  if ("template" in first) {
    return -1;
  }
  return 1;
};

export const genderFormatting = (gender) => {
  if (gender === "MALE") {
    return "Male";
  }
  if (gender === "FEMALE") {
    return "Female";
  }
  return "N/A";
};

export const monthYearFormatting = (str) => {
  if (str === "MONTHLY") {
    return VernacManager.translateString("jobcard_month");
  }
  return VernacManager.translateString("jobcard_year");
};

export const noticePeriodFormatting = (str) => {
  if (str) {
    return noticePeriodMapping[str];
  }
  return "";
};

export const vaccinationFormatting = (str) => {
  const vaccinationIndex = {
    "Not Vaccinated": "profile_not_vaccinated",
    "1st Dose": "profile_first_dose",
    "Fully Vaccinated": "profile_both_dose"
  };
  const vernacKey = vaccinationIndex[str];
  const finalString = VernacManager.translateString(vernacKey);
  if (str) {
    return finalString;
  }
  return "";
};

export const proficiencyFormatting = (str) => {
  const vernacKey = proficiencyMapping[str];
  const finalString = VernacManager.translateString(vernacKey);
  if (str) {
    return finalString;
  }
  return "";
};

export const experienceFormatting = (experience) => {
  const correctExperience = getCorrectExperience(experience);
  const vernacKey = experienceMapping[correctExperience];
  const finalString = VernacManager.translateString(vernacKey);
  if (correctExperience > 0) {
    return `${finalString}`;
  }
  return VernacManager.translateString("profile_no_experience");
};

export const processEducationArray = (educationArray) => {
  let sortedEducationArray = [];
  let index;
  let i;
  // Sorting the educationArray as per qualificationList
  for (i = 0; i < educationArray?.length; i += 1) {
    index = qualificationOrdering[educationArray[i].proficiency_level];
    sortedEducationArray[index] = educationArray[i];
  }
  // Removing empty properties
  sortedEducationArray = sortedEducationArray.filter(Boolean);
  return sortedEducationArray;
};

// required list of documents
const requiredDocumentIds = [1, 2, 3, 4, 5, 12, 11, 9, 13, 23, 24, 25, 26];

// required documents and the FAs for which it has to be shown to user
const documentSelectionConfig = {
  1: [154, 155, 29, 6, 31, 64, 118, 132, 1],
  2: [29],
  3: [29],
  9: [16, 15],
  12: [53, 78, 34, 33, 9, 26, 91, 89, 90],
  13: [31, 72, 1, 130],
  24: [154],
  25: [154],
  26: [154]
};

// documents asked to all users by default
const asktoAll = [4, 5, 11];

// ask to all - 4,5,11
// experienced - 9,12,23

export const filterDocumentArray = (
  documentArray = [],
  isExperienced,
  functionalAreas
) => {
  const faIds = functionalAreas?.map((item) => item.id) || [];
  const requiredDocs =
    documentArray?.filter((item) => requiredDocumentIds.includes(item.id)) ||
    [];

  let newDocumentArray = [];
  requiredDocs.forEach((oldItem) => {
    const item = {...oldItem};
    // documents shown by default in all cases
    if (asktoAll.includes(item.id)) {
      newDocumentArray.push(item);
    }
    // show document 23 to experienced users or is-owned is any other flows (cms/asr).
    else if (item.id === 12) {
      if (isExperienced || item.is_owned !== null) newDocumentArray.push(item);
    }
    // show documents 9 and 12 for users with experience and has fas selected which belongs to the reqired fas for this document or
    // is-owned is any other flows (cms/asr).
    else if (item.id === 9 || item.id === 12) {
      const isFaFound = documentSelectionConfig[item.id]?.some((fa) =>
        faIds.includes(fa)
      );
      if ((isFaFound && isExperienced) || item.is_owned !== null)
        newDocumentArray.push(item);
    }
    // show remaining documents if user has fas selected which belongs to the reqired fas for this document or
    // is-owned is any other flows (cms/asr).
    else {
      const isFaFound = documentSelectionConfig[item.id]?.some((fa) =>
        faIds.includes(fa)
      );
      if (isFaFound || item.is_owned !== null) newDocumentArray.push(item);
    }
  });

  newDocumentArray = newDocumentArray.map((item) => {
    const newItem = {...item}
    if (newItem.id === 13) {
      newItem.name = "Bank account proof";
    }
    return newItem;
  });
  return newDocumentArray;
};

export const processDocumentArray = (documentArray, candidateDocuments) => {
  let newDocumentArray = JSON.parse(JSON.stringify(documentArray));
  let i;
  let j;
  // Assign item of documentTemplate in its respective place using id of documentArray
  for (i = 0; i < newDocumentArray?.length; i += 1) {
    if (newDocumentArray[i].id in documentTemplate) {
      newDocumentArray[i].template = documentTemplate[newDocumentArray[i].id];
      // Assign proof value for template files using candidateDocuments
      for (j = 0; j < newDocumentArray[i]?.template?.files?.length; j += 1) {
        newDocumentArray[i].template.files[j].proofValue = getData(
          candidateDocuments,
          `${newDocumentArray[i].template.docField}.${newDocumentArray[i].template.files[j].proofField}`
        );
      }
    }
  }
  newDocumentArray = newDocumentArray.sort(sortByTemplate);
  return newDocumentArray;
};

export const getDateDifferenceInMonths = (date1, date2) => {
  const d1 = date1 ? new Date(date1) : new Date();
  const d2 = date2 ? new Date(date2) : new Date();
  let months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months -= d1.getMonth();
  months += d2.getMonth();
  return months <= 0 ? 0 : months;
};

export const getTotalExperience = (experienceArray = []) => {
  let result = 0;
  let i;
  for (i = 0; i < experienceArray?.length; i += 1) {
    result += getDateDifferenceInMonths(
      experienceArray[i].started_from,
      experienceArray[i].worked_till
    );
  }
  return result;
};

export const getCorrectDegree = (degree, proficiencyLevel) => {
  let result = "";
  if (!degree) {
    result = proficiencyFormatting(proficiencyLevel);
  } else {
    result = degree;
  }
  return result;
};

export const getCorrectExperience = (experience = 0) => {
  let result = 0;
  if (experience <= 0) result = 0;
  else if (experience <= 6) result = 6;
  else if (experience <= 12) result = 12;
  else if (experience <= 24) result = 24;
  else if (experience <= 36) result = 36;
  else if (experience <= 48) result = 48;
  else if (experience <= 60) result = 60;
  else result = 72;
  return result;
};

export const getFilenameFromURL = (filename, truncateLength = 18) => {
  let result = "";

  const index1 = filename.lastIndexOf("/");
  const index2 = filename.lastIndexOf(".");

  let name = filename.substring(index1 + 1, index2);
  if (name?.length > truncateLength) {
    name = `${name.substring(0, truncateLength)}..`;
  }

  const extension = filename.split("?")[0]?.substring(index2);

  result = `${name}${extension}`;

  return result;
};

export const getOverlayIconVisiblity = (item = {}) => {
  let result = item.is_owned !== null;
  let i;
  if (result && "template" in item) {
    for (i = 0; i < item.template.files?.length; i += 1) {
      if (!item.template.files[i].proofValue) {
        result = false;
        break;
      }
    }
  }
  return result;
};

export const getFAImage = (id) => {
  let result = "";
  const image = faImageMapping[id];
  if (image) {
    result = `${config.MEDIA_URL}fa-categories/${image}`;
  } else {
    result = defaultFAImage;
  }
  return result;
};

export const getExperienceOption = (experience) => {
  const correctExperience = getCorrectExperience(experience);
  return {
    label: VernacManager.translateString(experienceMapping[correctExperience]),
    value: correctExperience.toString()
  };
};

export const createPushProfileClevertap = (candId) => {
  const pushProfileClevertap = (eventName, eventProps = {}) => {
    pushClevertapEvent(eventName, {
      ...eventProps,
      Stakeholder_ID: candId,
      Stakeholder_Type: "candidate",
      platform:
        Platform.OS === "web" ? "candidate mobile web" : "candidate mobile app",
      timestamp: new Date().toString()
    });
  };

  return pushProfileClevertap;
};

export function changeNumberFormat(number) {
  if (typeof number !== "number") {
    return "";
  }
  let displayStr;
  let isPlural;

  // Rounds off digits to decimalPoints decimal places
  function roundOf(integer) {
    const numTemp = `${integer}`.split(".");
    const num =
      numTemp?.length === 2
        ? `${numTemp[0]}.${numTemp[1].substring(0, 1)}`
        : `${numTemp[0]}`;
    return +num;
  }

  if (number >= 1000 && number < 100000) {
    const thousands = roundOf(number / 1000);
    isPlural = number % 1000 > 0;
    displayStr = `${thousands}K${isPlural ? " +" : ""}`;
  } else if (number >= 100000 && number < 10000000) {
    const lakhs = roundOf(number / 100000);
    isPlural = number % 100000 > 0;
    displayStr = `${lakhs}L${isPlural ? " +" : ""}`;
  } else if (number >= 10000000) {
    let base = 10000000;
    const numStr = `${number}`;
    if (numStr.length > 9) {
      base = Math.pow(10, numStr.length - 2);
    }
    const crores = roundOf(number / base);
    isPlural = number % base > 0;
    displayStr = `${crores}CR${isPlural ? "+" : ""}`;
  } else {
    displayStr = roundOf(+number);
  }

  return displayStr;
}
